<template>
  <m-spinner
    v-if="chartDataQuery.loading.value"
    class="_spinner"
  />
  <m-table
    v-else
    :class="$attrs.class"
    :data-source="data"
    :columns="columns"
    sticky-header
  />
</template>

<script setup>
import useChartDisplay from '@/composables/chart-display';
import useGoalProgressChart from '@/composables/grid-page/chart/goal-progress-chart';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import { DateTime } from 'luxon';
import { aggregatorType, chartGroupByOption, gridPageChartType } from 'shared/constants.json';
import { computed, toRef } from 'vue';
import { edgeName } from '@/composables/grid-page/chart/utils';
import { getRoundedValue } from '@/lib/charts/format';
import { iconByType } from '@/lib/property';
import { sortAlphaNumeric } from 'shared/lib/sort';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  chart: {
    type: Object,
    required: true,
  },
  chartDataQuery: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const { userLang } = useLoggedInUser();

const { metricUnit, getValueText } = useChartDisplay();
const { calculateExpectedValue } = useGoalProgressChart(toRef(props.chartDataQuery, 'result'));

const isTimeSeriesMode = computed(() => props.chart.aggregationConfig?.aggregation?.aggregator === aggregatorType.progressOverTime);

const data = computed(() => {
  const result = props.chartDataQuery.result.value;
  if (result === null) {
    return [];
  }

  if (isTimeSeriesMode.value && result.isTimeSeries) {
    if (!result.progressData || result.progressData.length === 0) {
      return [];
    }

    return result.progressData
      .filter((point) => point.x >= result.startDate.toMillis() && point.x <= result.endDate.toMillis())
      .sort((a, b) => a.x - b.x)
      .map((point) => ({
        date: DateTime.fromMillis(point.x).toLocaleString(DateTime.DATE_MED),
        actual: `${getRoundedValue(point.y, 2)} %`,
        expected: `${calculateExpectedValue(point.x, result.startDate, result.endDate)} %`,
      }));
  }

  if (props.chart.chartType === gridPageChartType.metric) {
    return [{
      name: metricUnit(props.chart.aggregationConfig.aggregation, props.chart.source),
      y: result,
    }];
  }

  if (!Array.isArray(result.series)) {
    return [];
  }

  if (props.chart.chartType === gridPageChartType.pie) {
    return result.series[0].data;
  }

  return result.series.map((series) => {
    const row = { name: series.name ?? getValueText(props.chart.aggregationConfig.aggregation) };
    series.data.forEach((item, index) => {
      row[`col_${index}`] = item.y;
    });
    return row;
  });
});

const firstColumnTitle = computed(() => {
  if (props.chart.chartType === gridPageChartType.metric) {
    return t('gridPageChartDataTable.metric');
  }
  if (props.chart.chartType === gridPageChartType.pie) {
    return edgeName(props.chart.groupBy, t, userLang.value);
  }
  if (props.chart.aggregationConfig.stackOptions.groupBy === chartGroupByOption.none) {
    return t('gridPageChartDataTable.metric');
  }
  return edgeName(props.chart.aggregationConfig.stackOptions, t, userLang.value);
});

const firstColumnIcon = computed(() => {
  if (props.chart.chartType === gridPageChartType.metric || props.chart.groupBy === null) {
    return '';
  }

  const groupBy = props.chart.chartType === gridPageChartType.pie
    ? props.chart.groupBy
    : props.chart.aggregationConfig.stackOptions;

  if (groupBy.groupBy === chartGroupByOption.none) {
    return '';
  }

  return groupBy.isProperty
    ? iconByType({ type: groupBy.property.type })
    : iconByType({ type: groupBy.edgeType, edgeName: groupBy.edge });
});

const firstColumn = computed(() => ({
  key: 'name',
  dataIndex: 'name',
  title: firstColumnTitle.value,
  icon: firstColumnIcon.value,
  onFilter: (value, record) => record.name.toLowerCase().includes(value.toLowerCase()),
  sorter: sortAlphaNumeric,
  sticky: true,
}));

const columns = computed(() => {
  const result = props.chartDataQuery.result.value;

  if (isTimeSeriesMode.value && result?.isTimeSeries) {
    return [
      {
        key: 'date',
        dataIndex: 'date',
        title: t('gridPageChartDataTable.date'),
        sorter: sortAlphaNumeric,
        sticky: true,
      },
      {
        key: 'actual',
        dataIndex: 'actual',
        title: t('gridPageChartDataTable.actual'),
        sorter: sortAlphaNumeric,
      },
      {
        key: 'expected',
        dataIndex: 'expected',
        title: t('gridPageChartDataTable.expected'),
        sorter: sortAlphaNumeric,
      },
    ];
  }

  if (!Array.isArray(result?.series)) {
    return [];
  }

  const valueColumn = {
    key: 'value',
    dataIndex: 'y',
    sorter: sortAlphaNumeric,
  };

  if (props.chart.chartType === gridPageChartType.metric) {
    return [firstColumn.value, { ...valueColumn, title: t('gridPageChartDataTable.value') }];
  }

  if (props.chart.chartType === gridPageChartType.pie) {
    return [firstColumn.value, { ...valueColumn, title: getValueText(props.chart.aggregationConfig.aggregation, 'aggregator') }];
  }

  const sorter = result.series.length > 1 ? sortAlphaNumeric : undefined;
  return [
    firstColumn.value,
    ...(result.categories ?? []).map((category, index) => ({
      key: `col_${index}`,
      dataIndex: `col_${index}`,
      title: category,
      sorter,
    })),
  ];
});
</script>

<style scoped lang="scss">
._spinner {
  margin-top: 3rem;
}
</style>
